import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";

const COLONNES = [
  { id: "prospection",  label: "Prospection & Foncier",     color: "#7B68EE" },
  { id: "conception",   label: "Conception & Permis",        color: "#2E6DB4" },
  { id: "preparation",  label: "Préparation Chantier",       color: "#E67E22" },
  { id: "execution",    label: "Exécution",                  color: "#1A7A44" },
  { id: "reception",    label: "Réception & Livraison",      color: GOLD      },
  { id: "sav",          label: "SAV / Parfait Achèvement",   color: "#95A5A6" },
  { id: "bloque",       label: " Bloqué",                  color: "#C0392B" },
];

const SWIMLANES = [
  { id: "neuf",    label: "Projets Neufs"            },
  { id: "renov",   label: "Réhabilitations / Rénovations" },
  { id: "admin",   label: "Administratif & Général"  },
];

const PRIORITES = {
  urgent:  { label: " Urgent",       color: "#C0392B", bg: "#FDEDEC" },
  attente: { label: " En attente",   color: "#B7770D", bg: "#FEF9E7" },
  normal:  { label: " Normal",       color: "#1A7A44", bg: "#E8F6EF" },
};

const LOTS = ["Maçonnerie", "Plomberie", "Électricité", "Menuiserie", "Carrelage", "Peinture", "Toiture", "VRD", "Autre"];

const MODAL_INIT = {
  titre: "", description: "", colonne: "prospection", swimlane: "neuf",
  priorite: "normal", lot: "", date_echeance: "", projet_id: "",
  checklist: [], responsable_id: "",
};

export default function KanbanPage({ profile, isOPCMandate = false, cabinetId = null }) {
  void isOPCMandate;
  const [cards,       setCards]       = useState([]);
  const [projets,     setProjets]     = useState([]);
  const [membres,     setMembres]     = useState([]);
  const [modal,       setModal]       = useState(null);   // null | "new" | card object
  const [form,        setForm]        = useState(MODAL_INIT);
  const [filtreProjet, setFiltreProjet] = useState("");
  const [filtreLane,   setFiltreLane]   = useState("");
  const [loading,     setLoading]     = useState(false);
  const [drag,        setDrag]        = useState(null);   // card being dragged
  const [newCheck,    setNewCheck]    = useState("");

  const isAdmin = profile?.role === "admin";

  useEffect(() => { if (cabinetId == null) return; fetchAll(); }, [cabinetId]);

  async function fetchAll() {
    const [{ data: c }, { data: p }, { data: m }] = await Promise.all([
      supabase.from("kanban_cards").select("*, responsable:responsable_id(full_name, email), projet:projet_id(name)").eq("cabinet_id", cabinetId).order("created_at"),
      supabase.from("projects").select("id, name").eq("cabinet_id", cabinetId).order("name"),
      supabase.from("profiles").select("id, full_name, email, role").eq("cabinet_id", cabinetId).order("full_name"),
    ]);
    if (c) setCards(c);
    if (p) setProjets(p);
    if (m) setMembres(m);
  }

  function openNew(colonne, swimlane) {
    setForm({ ...MODAL_INIT, colonne, swimlane });
    setModal("new");
    setNewCheck("");
  }

  function openEdit(card) {
    setForm({
      titre: card.titre || "",
      description: card.description || "",
      colonne: card.colonne || "prospection",
      swimlane: card.swimlane || "neuf",
      priorite: card.priorite || "normal",
      lot: card.lot || "",
      date_echeance: card.date_echeance || "",
      projet_id: card.projet_id || "",
      checklist: card.checklist || [],
      responsable_id: card.responsable_id || "",
    });
    setModal(card);
    setNewCheck("");
  }

  async function handleSave() {
    if (!form.titre.trim()) return;
    setLoading(true);
    const payload = {
      ...form,
      projet_id: form.projet_id || null,
      responsable_id: form.responsable_id || null,
      updated_at: new Date().toISOString(),
    };
    if (modal === "new") {
      await supabase.from("kanban_cards").insert({ ...payload, cabinet_id: cabinetId });
    } else {
      await supabase.from("kanban_cards").update(payload).eq("id", modal.id);
    }
    await fetchAll();
    setModal(null);
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Supprimer cette carte ?")) return;
    await supabase.from("kanban_cards").delete().eq("id", id);
    await fetchAll();
    setModal(null);
  }

  async function handleDrop(colonneId) {
    if (!drag || drag.colonne === colonneId) return;
    await supabase.from("kanban_cards").update({ colonne: colonneId, updated_at: new Date().toISOString() }).eq("id", drag.id);
    setDrag(null);
    await fetchAll();
  }

  function addCheckItem() {
    if (!newCheck.trim()) return;
    setForm(f => ({ ...f, checklist: [...f.checklist, { label: newCheck.trim(), done: false }] }));
    setNewCheck("");
  }

  function toggleCheck(i) {
    setForm(f => {
      const cl = [...f.checklist];
      cl[i] = { ...cl[i], done: !cl[i].done };
      return { ...f, checklist: cl };
    });
  }

  function removeCheck(i) {
    setForm(f => ({ ...f, checklist: f.checklist.filter((_, j) => j !== i) }));
  }

  function isRetard(card) {
    if (!card.date_echeance) return false;
    return new Date(card.date_echeance) < new Date() && card.colonne !== "reception" && card.colonne !== "sav";
  }

  const cardsFiltered = cards.filter(c =>
    (!filtreProjet || c.projet_id === filtreProjet) &&
    (!filtreLane   || c.swimlane === filtreLane)
  );

  const lanes = filtreLane ? SWIMLANES.filter(l => l.id === filtreLane) : SWIMLANES;

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK }}>

      {/* En-tête */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}><div><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Projet</div><div style={{ fontSize: 24, fontWeight: 700 }}>Kanban Chantier</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Pilotage visuel de l'avancement des projets</div></div><div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}><select value={filtreProjet} onChange={e => setFiltreProjet(e.target.value)}
            style={{ padding: "7px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, color: INK, background: WHITE, outline: "none" }}><option value="">Tous les projets</option>
            {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select><select value={filtreLane} onChange={e => setFiltreLane(e.target.value)}
            style={{ padding: "7px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, color: INK, background: WHITE, outline: "none" }}><option value="">Toutes les lignes</option>
            {SWIMLANES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select></div></div>

      {/* Board */}
      <div style={{ overflowX: "auto", paddingBottom: 16 }}><div style={{ minWidth: COLONNES.length * 220 + 160 }}>

          {/* Header colonnes */}
          <div style={{ display: "flex", marginLeft: 140, gap: 8, marginBottom: 8 }}>
            {COLONNES.map(col => (
              <div key={col.id} style={{ width: 220, flexShrink: 0, textAlign: "center" }}><div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase",
                  color: WHITE, background: col.color, borderRadius: 6, padding: "5px 8px" }}>
                  {col.label}
                  <span style={{ marginLeft: 6, background: "rgba(255,255,255,0.25)", borderRadius: 999, padding: "1px 6px", fontSize: 9 }}>
                    {cardsFiltered.filter(c => c.colonne === col.id).length}
                  </span></div></div>
            ))}
          </div>

          {/* Swimlanes */}
          {lanes.map(lane => (
            <div key={lane.id} style={{ display: "flex", marginBottom: 8, gap: 8 }}>

              {/* Label swimlane */}
              <div style={{ width: 132, flexShrink: 0, display: "flex", alignItems: "flex-start", paddingTop: 8 }}><div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 9, fontWeight: 800,
                  letterSpacing: "0.15em", textTransform: "uppercase", color: INK_SOFT,
                  background: SOFT_BG, border: `1px solid ${BORDER}`, borderRadius: 6,
                  padding: "10px 6px", width: "100%", textAlign: "center" }}>
                  {lane.label}
                </div></div>

              {/* Colonnes */}
              {COLONNES.map(col => {
                const colCards = cardsFiltered.filter(c => c.colonne === col.id && c.swimlane === lane.id);
                return (
                  <div key={col.id}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => handleDrop(col.id)}
                    style={{ width: 220, flexShrink: 0, background: SOFT_BG,
                      border: `1px solid ${BORDER}`, borderRadius: 10,
                      padding: 8, minHeight: 120,
                      borderTop: `3px solid ${col.color}`,
                      transition: "background 0.15s",
                    }}>

                    {colCards.map(card => {
                      const p = PRIORITES[card.priorite] || PRIORITES.normal;
                      const retard = isRetard(card);
                      const checkDone = (card.checklist || []).filter(x => x.done).length;
                      const checkTotal = (card.checklist || []).length;
                      return (
                        <div key={card.id}
                          draggable
                          onDragStart={() => setDrag(card)}
                          onDragEnd={() => setDrag(null)}
                          onClick={() => openEdit(card)}
                          style={{
                            background: WHITE, border: `1px solid ${retard ? "#C0392B" : BORDER}`,
                            borderLeft: `3px solid ${retard ? "#C0392B" : col.color}`,
                            borderRadius: 8, padding: "10px 12px", marginBottom: 8,
                            cursor: "pointer", fontSize: 12,
                            boxShadow: retard ? "0 0 0 1px rgba(192,57,43,0.2)" : "0 1px 4px rgba(0,0,0,0.06)",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"}
                          onMouseLeave={e => e.currentTarget.style.boxShadow = retard ? "0 0 0 1px rgba(192,57,43,0.2)" : "0 1px 4px rgba(0,0,0,0.06)"}
                        >
                          {/* Priorité + lot */}
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 4 }}><span style={{ fontSize: 9, fontWeight: 700, background: p.bg, color: p.color, borderRadius: 999, padding: "2px 7px" }}>
                              {p.label}
                            </span>
                            {card.lot && (
                              <span style={{ fontSize: 9, color: INK_SOFT, background: SOFT_BG, borderRadius: 999, padding: "2px 7px", border: `1px solid ${BORDER}` }}>
                                {card.lot}
                              </span>
                            )}
                          </div>

                          {/* Titre */}
                          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, color: retard ? "#C0392B" : INK, lineHeight: 1.3 }}>
                            {retard && " "}{card.titre}
                          </div>

                          {/* Projet */}
                          {card.projet?.name && (
                            <div style={{ fontSize: 10, color: GOLD, fontWeight: 600, marginBottom: 4 }}>
                               {card.projet.name}
                            </div>
                          )}

                          {/* Footer */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                            {checkTotal > 0 && (
                              <span style={{ fontSize: 9, color: INK_SOFT }}>
                                 {checkDone}/{checkTotal}
                              </span>
                            )}
                            {card.date_echeance && (
                              <span style={{ fontSize: 9, color: retard ? "#C0392B" : INK_SOFT, fontWeight: retard ? 700 : 400 }}>
                                 {card.date_echeance}
                              </span>
                            )}
                            {card.responsable?.full_name && (
                              <div style={{ width: 22, height: 22, borderRadius: "50%", background: GOLD,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 9, fontWeight: 700, color: WHITE, flexShrink: 0 }}>
                                {card.responsable.full_name[0].toUpperCase()}
                              </div>
                            )}
                          </div></div>
                      );
                    })}

                    {/* Bouton ajouter */}
                    <button onClick={() => openNew(col.id, lane.id)}
                      style={{ width: "100%", padding: "7px", background: "transparent",
                        border: `1px dashed ${BORDER}`, borderRadius: 7, fontSize: 11,
                        color: INK_SOFT, cursor: "pointer", marginTop: 2 }}>
                      + Ajouter
                    </button></div>
                );
              })}
            </div>
          ))}
        </div></div>

      {/* ── Modal ── */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}><div style={{ background: WHITE, borderRadius: 14, width: "100%", maxWidth: 580,
            maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}><div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div style={{ fontSize: 15, fontWeight: 700 }}>{modal === "new" ? "Nouvelle carte" : "Modifier la carte"}</div><button onClick={() => setModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: INK_SOFT }}>×</button></div><div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

              {/* Titre */}
              <div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Titre *</label><input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                  placeholder="Titre de la tâche..."
                  style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, boxSizing: "border-box", outline: "none" }} /></div>

              {/* Description */}
              <div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} placeholder="Détails, contexte..."
                  style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, boxSizing: "border-box", outline: "none", resize: "vertical", fontFamily: "inherit" }} /></div>

              {/* Colonne + Swimlane */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Colonne</label><select value={form.colonne} onChange={e => setForm(f => ({ ...f, colonne: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}>
                    {COLONNES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select></div><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Ligne</label><select value={form.swimlane} onChange={e => setForm(f => ({ ...f, swimlane: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}>
                    {SWIMLANES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select></div></div>

              {/* Priorité + Lot */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Priorité</label><select value={form.priorite} onChange={e => setForm(f => ({ ...f, priorite: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}>
                    {Object.entries(PRIORITES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select></div><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Lot technique</label><select value={form.lot} onChange={e => setForm(f => ({ ...f, lot: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}><option value="">— Aucun —</option>
                    {LOTS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select></div></div>

              {/* Projet + Responsable */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Projet</label><select value={form.projet_id} onChange={e => setForm(f => ({ ...f, projet_id: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}><option value="">— Général —</option>
                    {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></div><div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Responsable</label><select value={form.responsable_id} onChange={e => setForm(f => ({ ...f, responsable_id: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, outline: "none" }}><option value="">— Aucun —</option>
                    {membres.map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
                  </select></div></div>

              {/* Date échéance */}
              <div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600 }}>Date d'échéance</label><input type="date" value={form.date_echeance} onChange={e => setForm(f => ({ ...f, date_echeance: e.target.value }))}
                  style={{ width: "100%", padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, color: INK, background: SOFT_BG, boxSizing: "border-box", outline: "none" }} /></div>

              {/* Checklist */}
              <div><label style={{ display: "block", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 8, fontWeight: 600 }}>
                  Checklist ({form.checklist.filter(x => x.done).length}/{form.checklist.length})
                </label><div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                  {form.checklist.map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={item.done} onChange={() => toggleCheck(i)}
                        style={{ accentColor: GOLD, width: 14, height: 14, flexShrink: 0 }} /><span style={{ flex: 1, fontSize: 13, textDecoration: item.done ? "line-through" : "none", color: item.done ? "#bbb" : INK }}>
                        {item.label}
                      </span><button onClick={() => removeCheck(i)}
                        style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button></div>
                  ))}
                </div><div style={{ display: "flex", gap: 8 }}><input value={newCheck} onChange={e => setNewCheck(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addCheckItem()}
                    placeholder="Ajouter un élément..."
                    style={{ flex: 1, padding: "7px 10px", border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, color: INK, background: SOFT_BG, outline: "none" }} /><button onClick={addCheckItem}
                    style={{ padding: "7px 14px", background: GOLD, color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    +
                  </button></div></div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                {modal !== "new" ? (
                  <button onClick={() => handleDelete(modal.id)}
                    style={{ padding: "9px 18px", background: "#FDEDEC", color: "#C0392B", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                     Supprimer
                  </button>
                ) : <div />}
                <div style={{ display: "flex", gap: 10 }}><button onClick={() => setModal(null)}
                    style={{ padding: "9px 18px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    Annuler
                  </button><button onClick={handleSave} disabled={loading || !form.titre.trim()}
                    style={{ padding: "9px 22px", background: GOLD, color: WHITE, border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: !form.titre.trim() ? 0.5 : 1 }}>
                    {loading ? "..." : modal === "new" ? "Créer" : "Sauvegarder"}
                  </button></div></div></div></div></div>
      )}
    </div>
  );
}